import { DocumentNode, FetchResult } from "@apollo/client";

import {
  CampaignManualDeleteOptOutMutation,
  CampaignManualDeleteOptOutMutationVariables,
  DeescalateThreadMutation,
  DeescalateThreadMutationVariables,
  EscalateThreadMutation,
  EscalateThreadMutationVariables,
  MarkThreadNeedsResponseMutation,
  MarkThreadNeedsResponseMutationVariables,
  MarkThreadNotNeedsResponseMutation,
  MarkThreadNotNeedsResponseMutationVariables,
  MarkThreadOptedOutMutation,
  MarkThreadOptedOutMutationVariables,
  SendMessageToThreadMutation,
  SendMessageToThreadMutationVariables,
  SetContactNoteMutation,
  SetContactNoteMutationVariables,
  UpdateCampaignContactTagsMutation,
  UpdateCampaignContactTagsMutationVariables,
} from "~frontend/__generated__/graphql";
import {
  UPDATE_CONTACT_TAGS,
  SET_CONTACT_NOTE,
} from "~frontend/graphql/campaignContacts";
import { LIST_SUBACCOUNT_ESCALATION_INBOXES } from "~frontend/graphql/inboxes";
import { CAMPAIGN_MANUAL_DELETE_OPT_OUT } from "~frontend/graphql/optOuts";
import {
  DEESCALATE_THREAD,
  ESCALATE_THREAD,
  MARK_THREAD_NEEDS_RESPONSE,
  MARK_THREAD_NOT_NEEDS_RESPONSE,
  MARK_THREAD_OPTED_OUT,
  SEND_MESSAGE_TO_THREAD,
} from "~frontend/graphql/threads";
import { useIntl } from "~frontend/providers/IntlProvider";
import useMutation from "~frontend/util/useMutation";

import { CampaignNameAndId, Message } from "./types";

export type ThreadMutations = {
  markThreadNeedsResponse: () => Promise<
    FetchResult<MarkThreadNeedsResponseMutation>
  >;
  markThreadNotNeedsResponse: () => Promise<
    FetchResult<MarkThreadNotNeedsResponseMutation>
  >;
  markThreadOptedOut: () => Promise<FetchResult<MarkThreadOptedOutMutation>>;
  markThreadNotOptedOut: () => Promise<
    FetchResult<CampaignManualDeleteOptOutMutation>
  >;
  sendMessage: (opts: {
    latestMessageId: string;
    message: Message;
    contactNote: string | null;
    applyTagIds: string[];
    removeTagIds: string[];
  }) => Promise<FetchResult<SendMessageToThreadMutation>>;
  setContactNote: (
    text: string
  ) => Promise<FetchResult<SetContactNoteMutation>>;
  updateContactTags: ({
    applyTagIds,
    removeTagIds,
  }: {
    applyTagIds: string[];
    removeTagIds: string[];
  }) => Promise<FetchResult<UpdateCampaignContactTagsMutation>>;
  escalate: (opts: {
    escalationInboxId: string;
  }) => Promise<FetchResult<EscalateThreadMutation>>;
  deescalate: () => Promise<FetchResult<DeescalateThreadMutation>>;
};

export default function useThreadMutations({
  threadId,
  contextCampaign,
  contextCampaignContactId,
  reloadThreadMutations,
}: {
  threadId: string;
  contextCampaign: CampaignNameAndId;
  contextCampaignContactId: string | null | undefined;
  reloadThreadMutations: (string | DocumentNode)[];
}): ThreadMutations {
  const intl = useIntl();

  const [markThreadNeedsResponse] = useMutation<
    MarkThreadNeedsResponseMutation,
    MarkThreadNeedsResponseMutationVariables
  >(MARK_THREAD_NEEDS_RESPONSE, {
    variables: {
      input: { threadId, campaignId: contextCampaign.id! },
    },
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to mark thread as needing a response",
      description:
        "Error message when we are unable to mark a thread as needing a response",
    }),
  });

  const [markThreadNotNeedsResponse] = useMutation<
    MarkThreadNotNeedsResponseMutation,
    MarkThreadNotNeedsResponseMutationVariables
  >(MARK_THREAD_NOT_NEEDS_RESPONSE, {
    variables: { input: { threadId } },
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to mark thread as not needing a response",
      description:
        "Error message when we are unable to mark a thread as not needing a response",
    }),
  });

  const [markThreadOptedOut] = useMutation<
    MarkThreadOptedOutMutation,
    MarkThreadOptedOutMutationVariables
  >(MARK_THREAD_OPTED_OUT, {
    variables: {
      input: { threadId, campaignId: contextCampaign.id },
    },
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to Opt Out contact",
      description: "Error message when we fail to opt-out a contact",
    }),
  });

  const [markThreadNotOptedOut] = useMutation<
    CampaignManualDeleteOptOutMutation,
    CampaignManualDeleteOptOutMutationVariables
  >(CAMPAIGN_MANUAL_DELETE_OPT_OUT, {
    refetchQueries: [
      ...reloadThreadMutations,
      "subaccountOptOutNumberDetails",
      "tenantOptOutNumberDetails",
    ],
    variables: { input: { threadId } },
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed opt in contact",
      description: "Error message when we fail to opt-in a contact",
    }),
    awaitRefetchQueries: true,
  });

  const [sendMessage] = useMutation<
    SendMessageToThreadMutation,
    SendMessageToThreadMutationVariables
  >(SEND_MESSAGE_TO_THREAD, {
    refetchQueries: reloadThreadMutations,
    awaitRefetchQueries: true,
  });

  const [setContactNote] = useMutation<
    SetContactNoteMutation,
    SetContactNoteMutationVariables
  >(SET_CONTACT_NOTE, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to save contact note",
      description: "Error message when we fail to save a contact note",
    }),
    refetchQueries: ["getActiveCampaignContact", ...reloadThreadMutations],
    awaitRefetchQueries: true,
  });

  const [updateContactTags] = useMutation<
    UpdateCampaignContactTagsMutation,
    UpdateCampaignContactTagsMutationVariables
  >(UPDATE_CONTACT_TAGS, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to update contact tags",
      description: "Error message when we fail to update tags",
    }),
    refetchQueries: reloadThreadMutations,
    awaitRefetchQueries: true,
  });

  const [escalate] = useMutation<
    EscalateThreadMutation,
    EscalateThreadMutationVariables
  >(ESCALATE_THREAD, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to escalate thread",
      description: "Error message when we fail to escalate a thread",
    }),
    refetchQueries: [
      ...reloadThreadMutations,
      LIST_SUBACCOUNT_ESCALATION_INBOXES,
    ],
    awaitRefetchQueries: true,
  });

  const [deescalate] = useMutation<
    DeescalateThreadMutation,
    DeescalateThreadMutationVariables
  >(DEESCALATE_THREAD, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to de-escalate thread",
      description: "Error message when we fail to de-escalate a thread",
    }),
    refetchQueries: [
      ...reloadThreadMutations,
      LIST_SUBACCOUNT_ESCALATION_INBOXES,
    ],
    awaitRefetchQueries: true,
  });

  return {
    markThreadNeedsResponse,
    markThreadNotNeedsResponse,
    markThreadOptedOut,
    markThreadNotOptedOut,
    sendMessage: ({
      latestMessageId,
      message,
      contactNote,
      applyTagIds,
      removeTagIds,
    }) =>
      sendMessage({
        variables: {
          input: {
            campaignId: contextCampaign.id ?? null,
            messageText: message.text,
            mediaAttachments: message.attachments,
            latestMessageId,
            updateContactNote: contactNote,
            applyTagIds,
            removeTagIds,
            threadId,
          },
        },
      }),
    setContactNote: (text: string) =>
      setContactNote({
        variables: {
          input: {
            campaignContactId: contextCampaignContactId!,
            text,
          },
        },
      }),
    updateContactTags: ({ applyTagIds, removeTagIds }) =>
      updateContactTags({
        variables: {
          input: {
            campaignContactId: contextCampaignContactId!,
            applyTagIds,
            removeTagIds,
          },
        },
      }),
    escalate: ({ escalationInboxId }) =>
      escalate({ variables: { input: { threadId, escalationInboxId } } }),
    deescalate: () => deescalate({ variables: { input: { threadId } } }),
  };
}
